import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import TestPreview from "../Exams/TestBuilder/TestPreview";
import { getQuestionsPreview } from "../../services/questionService";
import { getApiErrorMessage } from "../../utils/apiError";

// The server's cap on one preview.
export const MAX_PREVIEW = 100;

// What the preview left out, in words: drafts (shown once they are sent for review) and the questions it could not
// read (gone, or not the reader's); "" when nothing.
const skippedText = (t, skipped) => {
  const drafts = skipped.filter((item) => item.reason === "draft").length;
  const missing = skipped.length - drafts;
  return [
    drafts && t("my_questions.preview.skipped_drafts", { count: drafts }),
    missing && t("my_questions.preview.skipped_missing", { count: missing }),
  ]
    .filter(Boolean)
    .join(" ");
};

/**
 * "See as a test" for a list of questions: `open(uuids)` reads them as a student's test (GET questions/preview/,
 * nothing is saved) and shows it in TestPreview, with a line for the ones left out; nothing to show is said in a
 * toast instead. `loading` while it is read; `preview` is the element to render once in the page.
 */
export default function useQuestionsPreview() {
  const [t] = useTranslation();
  const [shown, setShown] = useState(null);
  const [loading, setLoading] = useState(false);

  const open = useCallback(
    async (uuids) => {
      if (!uuids.length || loading) return;
      if (uuids.length > MAX_PREVIEW) {
        toast.error(t("errors.too_many_questions"));
        return;
      }
      setLoading(true);
      try {
        const data = await getQuestionsPreview(uuids);
        const skipped = Array.isArray(data?.skipped) ? data.skipped : [];
        const rows = Array.isArray(data?.test_questions) ? data.test_questions : [];
        if (!rows.length) {
          toast.info(skipped.length && skipped.every((item) => item.reason === "draft") ? t("my_questions.preview.nothing_drafts") : t("my_questions.preview.nothing"));
          return;
        }
        setShown({
          test: { name: t("my_questions.preview.name", { count: rows.length }), test_questions: rows },
          passages: Array.isArray(data?.passages) ? data.passages : [],
          notice: skippedText(t, skipped),
        });
      } catch (err) {
        toast.error(getApiErrorMessage(t, err, "my_questions.preview.failed"));
      } finally {
        setLoading(false);
      }
    },
    [loading, t]
  );

  const preview = useMemo(
    () => (
      <TestPreview
        open={!!shown}
        test={shown?.test}
        passages={shown?.passages}
        notice={shown?.notice}
        badge={t("my_questions.preview.badge")}
        hint={t("my_questions.preview.hint")}
        onClose={() => setShown(null)}
      />
    ),
    [shown, t]
  );

  return { open, loading, preview };
}
